import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { 
  Home, 
  Folder, 
  Tag, 
  User, 
  X,
  Plus,
  Search
} from 'lucide-react';
import { theme } from '../../styles/theme';
import { useResponsiveSidebar } from '../../hooks/useResponsiveSidebar';

const SidebarContainer = styled.aside`
  position: fixed;
  top: 0;
  left: 0;
  height: 100vh;
  width: 280px;
  background-color: ${({ theme }) => theme.colors.surface};
  border-right: 1px solid ${({ theme }) => theme.colors.border};
  transform: ${({ $isOpen }) => $isOpen ? 'translateX(0)' : 'translateX(-100%)'};
  transition: transform 0.3s ease-in-out;
  z-index: ${({ theme }) => theme.zIndex.fixed};
  display: flex;
  flex-direction: column;
  overflow-y: auto;
  overflow-x: hidden;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.lg}) {
    transform: ${({ $isOpen }) => $isOpen ? 'translateX(0)' : 'translateX(-100%)'};
    width: 100%;
    max-width: 320px;
  }
  
  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    width: 100%;
    max-width: 280px;
  }
`;

const SidebarHeader = styled.div`
  padding: ${({ theme }) => theme.spacing.lg};
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const SidebarTitle = styled.h2`
  font-size: ${({ theme }) => theme.typography.fontSize.lg};
  font-weight: ${({ theme }) => theme.typography.fontWeight.bold};
  color: ${({ theme }) => theme.colors.text};
  margin: 0;
`;

const CloseButton = styled.button`
  display: none;
  background: none;
  border: none;
  padding: ${({ theme }) => theme.spacing.sm};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  color: ${({ theme }) => theme.colors.gray600};
  cursor: pointer;
  
  &:hover {
    background-color: ${({ theme }) => theme.colors.gray100};
  }
  
  @media (max-width: ${({ theme }) => theme.breakpoints.lg}) {
    display: block;
  }
`;

const Navigation = styled.nav`
  flex: 1;
  padding: ${({ theme }) => theme.spacing.md} 0;
`;

const NavSection = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing.lg};
`;

const SectionTitle = styled.h3`
  font-size: ${({ theme }) => theme.typography.fontSize.xs};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.gray500};
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin: 0 0 ${({ theme }) => theme.spacing.sm} 0;
  padding: 0 ${({ theme }) => theme.spacing.lg};
`;

const NavItem = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.md};
  padding: ${({ theme }) => theme.spacing.sm} ${({ theme }) => theme.spacing.lg};
  background: none;
  border: none;
  text-align: left;
  cursor: pointer;
  color: ${({ $active, theme }) => 
    $active ? theme.colors.primary : theme.colors.gray600
  };
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  font-weight: ${({ $active, theme }) => 
    $active ? theme.typography.fontWeight.medium : theme.typography.fontWeight.normal
  };
  transition: all 0.2s ease-in-out;
  
  &:hover {
    background-color: ${({ theme }) => theme.colors.gray100};
    color: ${({ $active, theme }) => 
      $active ? theme.colors.primary : theme.colors.gray700
    };
  }
  
  ${({ $active, theme }) => $active && `
    background-color: ${theme.colors.primaryLight}20;
    border-right: 3px solid ${theme.colors.primary};
  `}
`;

const QuickActions = styled.div`
  padding: ${({ theme }) => theme.spacing.lg};
  border-top: 1px solid ${({ theme }) => theme.colors.border};
`;

const QuickActionButton = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.sm};
  padding: ${({ theme }) => theme.spacing.sm} ${({ theme }) => theme.spacing.md};
  background-color: ${({ theme }) => theme.colors.primary};
  color: ${({ theme }) => theme.colors.white};
  border: none;
  border-radius: ${({ theme }) => theme.borderRadius.md};
  cursor: pointer;
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  transition: background-color 0.2s ease-in-out;
  
  &:hover {
    background-color: ${({ theme }) => theme.colors.primaryDark};
  }
`;

const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: ${({ theme }) => theme.zIndex.fixed - 1};
  display: ${({ $isOpen }) => $isOpen ? 'block' : 'none'};
  
  @media (min-width: ${({ theme }) => theme.breakpoints.lg}) {
    display: none;
  }
`;

const navigationItems = [
  {
    section: 'Main',
    items: [
      { icon: Home, label: 'Dashboard', path: '/dashboard' },
      { icon: Folder, label: 'Spaces', path: '/spaces' },
      { icon: Tag, label: 'Labels', path: '/labels' },
    ]
  },
  {
    section: 'Account',
    items: [
      { icon: User, label: 'Profile', path: '/profile' },
    ]
  }
];

function Sidebar({ isOpen, onToggle }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { closeSidebar } = useResponsiveSidebar();

  const handleNavClick = (path) => {
    navigate(path);
    // Close sidebar on mobile after navigation
    if (window.innerWidth < parseInt(theme.breakpoints.lg)) {
      closeSidebar();
    }
  };

  const handleOverlayClick = () => {
    onToggle();
  };

  return (
    <>
      <Overlay $isOpen={isOpen} onClick={handleOverlayClick} />
      <SidebarContainer $isOpen={isOpen}>
        <SidebarHeader>
          <SidebarTitle>Bunkr</SidebarTitle>
          <CloseButton onClick={onToggle}>
            <X size={20} />
          </CloseButton>
        </SidebarHeader>

        <Navigation>
          {navigationItems.map((section) => (
            <NavSection key={section.section}>
              <SectionTitle>{section.section}</SectionTitle>
              {section.items.map((item) => {
                const Icon = item.icon;
                const isActive = location.pathname === item.path;
                
                return (
                  <NavItem
                    key={item.path}
                    $active={isActive}
                    onClick={() => handleNavClick(item.path)}
                  >
                    <Icon size={18} />
                    {item.label}
                  </NavItem>
                );
              })}
            </NavSection>
          ))}
        </Navigation>

        <QuickActions>
          <QuickActionButton onClick={() => handleNavClick('/spaces')}>
            <Plus size={16} />
            New Space
          </QuickActionButton>
        </QuickActions>
      </SidebarContainer>
    </>
  );
}

export default Sidebar;
